/**
 * Pinch with hysteresis and a release grace — the one pinch every VR build
 * tool reads (grab, two-hand, held item, ray placement).
 *
 * Raw inputs each frame: the runtime's pinch (WebXR select, which Quest
 * already debounces a little) and the thumb-tip ↔ index-tip distance.
 *
 *   start    select, or the tips closer than PINCH_START
 *   hold     select, or the tips still closer than PINCH_RELEASE (looser), for
 *            at most PINCH_DIST_HOLD_MS after select let go — so a half-open
 *            hand or a bad joint read can't keep a grab alive forever
 *   release  only after the raw pinch has been gone for PINCH_GRACE_MS, so a
 *            1–3 frame tracking blip (or a jittery distance) never drops what
 *            you're holding. Same 120 ms as the hand teleport's grace
 *            (VRHandControls), which imports it from here.
 *
 * `consume()` marks the current pinch as used (e.g. it just dropped the held
 * item) so no other tool can start something with it; it clears on release.
 *
 * Pure (no three.js, no DOM): unit-tested in tests/vrPinch.test.ts.
 */

export const PINCH_START = 0.015;       // m between thumb and index tips
export const PINCH_RELEASE = 0.035;     // m — looser than start (hysteresis)
export const PINCH_GRACE_MS = 120;
export const PINCH_DIST_HOLD_MS = 500;
/**
 * The pinch point (between thumb and index tips) still shifts a few cm while
 * the fingers finish closing. Tools re-take their starting pose during this
 * window after a pinch starts, so closing your fingers doesn't nudge, turn or
 * resize what you grabbed.
 */
export const PINCH_SETTLE_MS = 120;

export class PinchFilter {
  /** Filtered pinch: true from start until the grace after a release ran out. */
  held = false;
  /** When the current pinch started (ms). */
  startedAt = 0;
  /** This pinch was used up by a tool (see consume). */
  consumed = false;
  /** Number of pinches started so far — tools compare it to spot a new one. */
  count = 0;
  private upSince = 0;      // raw pinch gone since (0 = it's down)
  private selectUpAt = 0;   // select let go at (distance-only hold since)

  /**
   * @param now     ms (performance.now())
   * @param select  the runtime's pinch (WebXR select) for this hand
   * @param dist    thumb ↔ index tip distance in m, or null when the hand isn't tracked
   */
  update(now: number, select: boolean, dist: number | null): boolean {
    let raw: boolean;
    if (select) { raw = true; this.selectUpAt = 0; }
    else if (dist === null) raw = false;
    else if (!this.held) raw = dist < PINCH_START;
    else {
      if (!this.selectUpAt) this.selectUpAt = now;
      raw = dist < PINCH_RELEASE && now - this.selectUpAt < PINCH_DIST_HOLD_MS;
    }

    if (raw) {
      this.upSince = 0;
      if (!this.held) { this.held = true; this.startedAt = now; this.consumed = false; this.count++; }
    } else if (this.held) {
      if (!this.upSince) this.upSince = now;
      if (now - this.upSince >= PINCH_GRACE_MS) { this.held = false; this.consumed = false; this.upSince = 0; this.selectUpAt = 0; }
    }
    return this.held;
  }

  /** Held, and not used up by another tool this pinch. */
  get free() { return this.held && !this.consumed; }
  /** In the release grace: the raw pinch is gone but it still counts as held. */
  get releasing() { return this.held && this.upSince > 0; }
  consume() { if (this.held) this.consumed = true; }
  reset() { this.held = false; this.consumed = false; this.upSince = 0; this.selectUpAt = 0; }
}
